import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { useWalletSignIn } from '../lib/queries';
import { Button, Logo, Spinner } from '../components/ui';

/**
 * Where the "my account" link on the wallet card lands.
 *
 * Unlike the staff-issued link this one is reusable — the card keeps it for as
 * long as it is on the phone — so there is nothing to burn and reopening it is
 * ordinary. What expires is the session it opens, half an hour after the last
 * thing the holder does here.
 */
export function WalletAccessPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useI18n();
  const navigate = useNavigate();
  const signIn = useWalletSignIn();

  // StrictMode runs this twice in development; one sign-in is enough.
  const attempted = useRef<string | null>(null);

  useEffect(() => {
    if (!token || attempted.current === token) return;
    attempted.current = token;
    // Replaced rather than pushed, so Back does not walk into the raw token.
    signIn.mutate(token, { onSuccess: () => navigate('/', { replace: true }) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-sm flex-col items-center justify-center gap-6 px-6 text-center">
      <Logo size="lg" />
      {(signIn.isPending || signIn.isSuccess) && (
        <>
          <Spinner />
          <p className="text-sm text-ink-dim">{t('login.linkWorking')}</p>
        </>
      )}
      {signIn.isError && (
        <>
          <p className="text-[0.95rem] leading-relaxed text-ink-dim">{t('login.cardInvalid')}</p>
          <Button variant="ghost" onClick={() => { window.location.href = '/'; }}>
            {t('login.title')}
          </Button>
        </>
      )}
    </main>
  );
}
